import { useCallback } from 'react';
import { useEvent } from '../../private/hooks/useEvent';

type EventHandler = (event: KeyboardEvent) => void;
type KeyFilterPredicate = (evt: KeyboardEvent) => boolean;
type KeyFilter = KeyFilterPredicate | boolean | string | null | undefined;

type UseKeyOptions = {
  eventName?: string;
  eventTarget?: Parameters<typeof useEvent>[2];
  eventOptions?: Parameters<typeof useEvent>[3];
};

export const useKey = (
  key: KeyFilter,
  eventHandler: EventHandler,
  { eventName = 'keydown', eventTarget, eventOptions }: UseKeyOptions = {},
) => {
  const memoizedEventHandler = useCallback(
    (handlerEvent: KeyboardEvent) => {
      // key events that happen *inside* an input should be ignored; this is to avoid issues
      // where typing into an input is inadvertently triggering actions the user does not want
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.nodeName ?? '') ||
        (document.activeElement as HTMLElement | null)?.isContentEditable
      ) {
        return;
      }

      let matchesKey = false;

      // key arg is a function so just return the function
      if (typeof key === 'function') {
        matchesKey = key(handlerEvent);
      } else if (typeof key === 'string') {
        // key arg is probably a KeyboardEvent key
        matchesKey = handlerEvent.key === key;
      } else if (key) {
        // any truthy, non-string key
        matchesKey = true;
      } else {
        // key arg is falsey
        matchesKey = false;
      }

      if (matchesKey) {
        eventHandler(handlerEvent);
      }
    },
    [key, eventHandler],
  );

  useEvent(eventName, memoizedEventHandler, eventTarget, eventOptions);
};
